import { useState, useEffect, useContext } from 'react';
import { Chart } from 'react-google-charts';
import UserUsageTable from './AnalyticsViews/UserUsageTable';
import TipSheetUsageTable from './AnalyticsViews/TipSheetUsageTable';
import { UserContext } from '../../../hooks/UserContext';
import useUpdateUserProfile from '../../../hooks/useUpdateUserProfile';

export default function UserAnalyticsTable({
  handleSwitchTableView,
  users,
  getLogs,
  setMessage,
  setActionModal,
  setActionSuccess,
}) {
  const barOptions = {
    isStacked: true,
    chartArea: {
      width: '70%',
      height: '90%',
      top: 20,
      bottom: 100,
      left: 250,
      right: 20,
    },
    fontSize: 14,
    colors: ['#028AD1', '#BE2C25', '#D9D9D9'],
    hAxis: {
      title: '# Requests',
    },
    vAxis: {},
    bar: { groupWidth: '90%' },
    legend: { position: 'none' },
  };
  const lineOptions = {
    chartArea: {
      width: '80%',
      height: '65%',
      top: 20,
      bottom: 100,
      left: 100,
      right: 20,
    },
    fontSize: 16,
    legend: { position: "none" },
    hAxis: { title: "Week of", slantedText: true, slantedTextAngle: 45, },
  };
  const lineRequestsOptions = {
    ...lineOptions,
    vAxis: { title: '# of Requests' },
  };
  const lineUniqueUsersOptions = {
    ...lineOptions,
    vAxis: { title: '# of Unique Visitors' },
  };

  const { updateProfileWithoutPush } = useUpdateUserProfile();
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [parsedLogs, setParsedLogs] = useState([]);
  const [tipSheetRequestLogs, setTipSheetRequestLogs] = useState([]);

  const [view, setView] = useState('table_users');
  const viewOptions = [
    { label: "Users (Table)", value: "table_users" },
    { label: "Users (Bar)", value: "bar_users" },
    { label: "Total Requests (Line)", value: "line_requests" },
    { label: "Unique Visitors (Line)", value: "line_unique_users" },
    { label: "Tip Sheets", value: "table_tipsheets" },
  ]
  const [dateLimit, setDateLimit] = useState(30);
  const dateOptions = [
    { label: "Last 7 Days", value: 7 },
    { label: "Last 30 Days", value: 30 },
    { label: "Last 60 Days", value: 60 },
    { label: "All Time", value: -1 },
  ];
  const [trendLineDateLimit, setTrendLineDateLimit] = useState(-1);
  const trendLineDateOptions = [
    { label: "Last 30 Days", value: 30 },
    { label: "Last 60 Days", value: 60 },
    { label: "Last 90 Days", value: 90 },
    { label: "Last 180 Days", value: 180 },
    { label: "Last Year", value: 365 },
    { label: "All Time", value: -1 },
  ];

  const [includeAdminData, setIncludeAdminData] = useState(false);
  const [includeAdminDataLoading, setIncludeAdminDataLoading] = useState(false);

  const validAccessTypes = ['Index', 'Archive', 'Search', 'Tip Sheet'];

  const parseLogFile = (logText, users, includeAdminData) => {
    const logLines = logText.split('\n');
    const parsedLogs = [];
    const tipSheetRequestLogs = [];

    const accessLogEntryRegex = /^\[(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\]\s+([A-Za-z ]+)\s+\|\s+([^|]+)\s+\|/;
    const tipSheetRequestRegex = /\|\s+(\/[^| ]+)\s+\|/;

    for (const line of logLines) {
      if (line.includes('Log Format::') || !line.trim()) continue;

      const match = line.trim().match(accessLogEntryRegex);
      if (!match) {
        continue;
      };

      const [, date, accessType, email] = match;

      // remove logs not tagged with any email
      if (email.trim() === 'None' || email.trim() === '-1') {
        continue;
      }

      // try to match log with associated user/profile from DB
      const matchingUser = users.find(user => user.user__email === email.trim());

      // filter out logs generated by admins (only if match found)
      if (!includeAdminData && matchingUser?.user__is_staff) {
        continue;
      }

      // format/save logs for further processing
      if (validAccessTypes.includes(accessType.trim())) {
        parsedLogs.push({
          date,
          accessType: accessType.trim(),
          email: email.trim(),
          name: matchingUser?.name || "n/a",
        });
      }

      // separately/additionally save tip sheet requests. use more specific regex to get request URL path
      if (accessType === 'Tip Sheet') {
        const TSmatch = line.trim().match(tipSheetRequestRegex);
        if (!TSmatch) {
          continue;
        };

        const [, path] = TSmatch;
        tipSheetRequestLogs.push({
          date,
          path: path.trim(),
        })
      }
    }

    return { parsedLogs, tipSheetRequestLogs };
  }

  const getRequestsByUser = (parsedLogs, dateLimit = 30) => {
    let recentLogs = parsedLogs;

    // filter out entries older than dateLimit days ago (default 30)
    if (dateLimit > 0) {
      const DAYS_MS = dateLimit * 24 * 60 * 60 * 1000;
      const now = new Date();
      recentLogs = parsedLogs.filter(log => {
        const logDate = new Date(log.date);
        return now - logDate <= DAYS_MS;
      });
    }

    // create a dictionary {email: {accessType: int}}
    const accessCounts = {};

    // iterate over date-filtered parsedLogs array, incrementing accessType for email
    for (const log of recentLogs) {
      const { email, accessType, name } = log;

      if (!accessCounts[email]) {
        accessCounts[email] = { email: email, name: name };
        for (const accessType of validAccessTypes) {
          accessCounts[email][accessType] = 0;
        }
      }

      accessCounts[email][accessType] += 1;
    }

    // convert JS object to array
    return Object.values(accessCounts);
  }

  const getRequestsByWeek = (parsedLogs, trendLineDateLimit) => {
    // parsedLogs: array of {date, accessType, email} objects representing single log entries
    // filter out logs older than trendLineDateLimit (default all time -1)
    let recentLogs = parsedLogs;
    const firstLogDate = recentLogs[0].date;
    if (trendLineDateLimit > 0) {
      const DAYS_MS = trendLineDateLimit * 24 * 60 * 60 * 1000;
      const now = new Date();
      recentLogs = parsedLogs.filter(log => {
        const logDate = new Date(log.date);
        return now - logDate <= DAYS_MS;
      });
    }

    const requestsByWeek = {}; // {datestring: int}
    const uniqueUsersByWeek = {}; // {datestring: set}

    // iterate through logs, incrementing dictionary for the week (key is the date for start of that week)
    for (const log of recentLogs) {
      const date = new Date(log.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // start of week (sunday)
      weekStart.setHours(0, 0, 0, 0);

      const key = weekStart.toISOString().slice(0, 10);  // "YYYY-MM-DD"
      requestsByWeek[key] = (requestsByWeek[key] || 0) + 1;
      if (!uniqueUsersByWeek[key]) {
        uniqueUsersByWeek[key] = new Set();
      }
      uniqueUsersByWeek[key].add(log.email);
    }

    // sum the unique users for each week
    for (const key in uniqueUsersByWeek) {
      uniqueUsersByWeek[key] = uniqueUsersByWeek[key].size
    }

    return { requestsByWeek, uniqueUsersByWeek, firstLogDate }
  }

  const getTopNRequestedTipSheets = (tipSheetRequestLogs, dateLimit = 30, n = 50) => {
    // filter out entries older than dateLimit days ago (default 30)
    let recentLogs = tipSheetRequestLogs;
    if (dateLimit > 0) {
      const DAYS_MS = dateLimit * 24 * 60 * 60 * 1000;
      const now = new Date();
      recentLogs = tipSheetRequestLogs.filter(log => {
        const logDate = new Date(log.date);
        return now - logDate <= DAYS_MS;
      });
    }

    // create a dictionary to count accesses {path: int}
    const accessCounts = {};

    // iterate over date-filtered request logs array, incrementing counts
    for (const log of recentLogs) {
      const { date, path } = log;

      if (!accessCounts[path]) {
        accessCounts[path] = 0;
      }

      accessCounts[path] += 1;
    }

    // sort and return top N most requested TS from the dict => [id:str, count:int]
    let requests = Object.keys(accessCounts).map((key) => [key.replace('/api/', '').replace(/\/$/, ''), accessCounts[key]]); // remove /api/ and trailing slash
    requests.sort((a, b) => b[1] - a[1]);
    return requests.slice(0, n);
  }

  const formatBarUserData = (requestsByUser) => {
    const formattedData = requestsByUser.map(user => [user["email"], user["Index"], user["Tip Sheet"]]);
    const headers = [["Email", "Main Page", "Tip Sheet"]];

    return headers.concat(formattedData);
  }

  const formatLineGraphData = (yAxisTitle, weeklyTotals, trendLineDateLimit, firstLogDate) => {
    // loop over weeks from __ days ago to present, get weekly request count from dict or 0
    const formattedData = [["Week of", yAxisTitle]];
    let cur;
    if (trendLineDateLimit < 0) { // start with date of earliest log for all time
      cur = new Date(firstLogDate);
    } else { // start with date trendLineDateLimit days ago
      cur = new Date();
      cur.setDate(cur.getDate() - trendLineDateLimit);
    }
    cur.setDate(cur.getDate() - cur.getDay()); // we are indexing weeks by the first day of the week (sunday)
    cur.setHours(0, 0, 0, 0);

    const end = new Date();
    end.setDate(end.getDate() - end.getDay()); // present (week start)
    end.setHours(0, 0, 0, 0);

    while (cur <= end) {
      const key = cur.toISOString().slice(0, 10);  // "YYYY-MM-DD"
      formattedData.push([key, weeklyTotals[key] || 0]);

      cur.setDate(cur.getDate() + 7);
    }

    return formattedData;
  }

  const exportCSV = (view, includeAdminData, dateLimit, trendLineDateLimit) => {
    if (view === 'table_users' || view === 'table_tipsheets') {
      exportTableCSV(view, includeAdminData, dateLimit);
    } else if (view === 'line_requests' || view === 'line_unique_users') {
      exportLineCSV(view, includeAdminData, trendLineDateLimit);
    }
  }

  const exportTableCSV = (view, includeAdminData, dateLimit) => {
    const rows = document.querySelectorAll('tr'); // get all rows in table
    let csv = []; // container for table as array of comma-separated row data

    for (let i = 0; i < rows.length; i++) {
      const row = [];
      const cols = rows[i].querySelectorAll('td, th'); // get all data & heading values

      for (let j = 0; j < cols.length; j++) {
        row.push(cols[j].innerText);
      }

      csv.push(row.join(',')); // join values as comma-separated string, append to csv array
    }

    csv = csv.join('\n'); // convert array to newline-separated string

    // search dateOptions for dateLimit label
    const currentDateLimit = dateOptions.find(opt => opt.value === dateLimit)?.label || "Unknown Date Range";

    // create CSV file and temporary link to trigger download
    const CSVFile = new Blob([csv], { type: "text/csv" });
    const temp_link = document.createElement('a');
    let tableType = '';
    if (view === 'table_users') {
      tableType = 'User';
    } else if (view === 'table_tipsheets') {
      tableType = 'TipSheet';
    }
    let admins = '';
    if (includeAdminData) {
      admins = 'UsersAndAdmins';
    } else {
      admins = 'UsersOnly';
    }
    temp_link.download = `AITipSheets${tableType}UsageData(${admins})${new Date().toLocaleDateString()}-${currentDateLimit.replace(/\s+/g, '-').toLowerCase()}.csv`;
    const url = window.URL.createObjectURL(CSVFile);
    temp_link.href = url;
    document.body.appendChild(temp_link);
    temp_link.click();
    document.body.removeChild(temp_link);
  }

  const exportLineCSV = (view, includeAdminData, trendLineDateLimit) => {
    let lineGraphData = [];
    if (view === 'line_requests') {
      lineGraphData = lineRequestsData;
    } else if (view === 'line_unique_users') {
      lineGraphData = lineUniqueUsersData;
    }
    if (lineGraphData.length <= 0) {
      return;
    }

    let csv = [];
    for (let i = 0; i < lineGraphData.length; i++) {
      csv.push(lineGraphData[i].join(',')); // append comma-separated string to csv arr
    }

    csv = csv.join('\n'); // convert array to newline-separated string

    // search dateOptions for dateLimit label
    const currentDateLimit = trendLineDateOptions.find(opt => opt.value === trendLineDateLimit)?.label || "Unknown Date Range";

    // create CSV file and temporary link to trigger download
    const CSVFile = new Blob([csv], { type: "text/csv" });
    const temp_link = document.createElement('a');
    let tableType = '';
    if (view === 'line_requests') {
      tableType = 'TotalRequests';
    } else if (view === 'line_unique_users') {
      tableType = 'UniqueVisitors';
    }
    let admins = '';
    if (includeAdminData) {
      admins = 'UsersAndAdmins';
    } else {
      admins = 'UsersOnly';
    }
    temp_link.download = `AITipSheetsHistorical${tableType}TrendData(${admins})${new Date().toLocaleDateString()}-${currentDateLimit.replace(/\s+/g, '-').toLowerCase()}.csv`;
    const url = window.URL.createObjectURL(CSVFile);
    temp_link.href = url;
    document.body.appendChild(temp_link);
    temp_link.click();
    document.body.removeChild(temp_link);
  }

  const handleToggleIncludeAdminData = async () => {
    setIncludeAdminDataLoading(true);
    try {
      const toggleValue = !includeAdminData;
      const success = await updateProfileWithoutPush({ ...user, include_admin_data_analytics: toggleValue });
      if (success) {
        setIncludeAdminData(toggleValue);
        user.include_admin_data_analytics = toggleValue;
      } else {
        setActionSuccess(false);
        setMessage(`Error occurred while updating table settings in profile.`);
        setActionModal(true);
      }
    } catch (error) {
      setActionSuccess(false);
      setMessage(`Error occurred while updating table settings in profile.`);
      setActionModal(true);
    } finally {
      setIncludeAdminDataLoading(false);
    }
  }

  /*
  we can technically control the input directly from the context value (removing
  a stale render cycle when the user object is updated), but there are drawbacks.
  "Context is great for reading values but not so great when updating values
  due to the re-rendering."
  "React will re-render every child that subscribes to context when any of the
  state in that context is changed."
  https://www.reddit.com/r/reactjs/comments/1868pjm/comment/kb6okml/?utm_source=share&utm_medium=web3x&utm_name=web3xcss&utm_term=1&utm_content=share_button
  since we always update the local state and the context value at the same time
  anyways for a checkbox it matters less, but with a text input for example we
  would be causing tons of intermediate re-renders for unrelated components
  */
  useEffect(() => {
    setIncludeAdminData(user.include_admin_data_analytics);
  }, [user]);

  useEffect(() => {
    const fetchUserAccessData = async (users, includeAdminData) => {
      setLoading(true);
      try {
        const rawAccessLogs = await getLogs("access", -1); // get full access log file
        const { parsedLogs, tipSheetRequestLogs } = parseLogFile(rawAccessLogs, users, includeAdminData);
        setParsedLogs(parsedLogs);
        setTipSheetRequestLogs(tipSheetRequestLogs);
      } catch (error) {
        console.error("Failed to fetch access logs", error);
      } finally {
        setLoading(false);
      }
    }

    fetchUserAccessData(users, includeAdminData);
  }, [users, includeAdminData]);

  // note: can memoize this logic dependent on [dateLimit, parsedLogs] if gets slow (same for topRequests)
  let userData = [];
  let barUserData = [];
  let lineRequestsData = [];
  let lineUniqueUsersData = [];
  if (parsedLogs.length > 0) {
    const requestsByUser = getRequestsByUser(parsedLogs, dateLimit); // count requests-per-user from the parsed logs
    requestsByUser.sort((a, b) => b["Index"] + b["Tip Sheet"] - a["Index"] - a["Tip Sheet"]) // sort users descending by combined number of requests
    userData = requestsByUser; // requests-per-user (table)
    barUserData = formatBarUserData(requestsByUser); // format data for requests-per-user (bar chart)

    const { requestsByWeek, uniqueUsersByWeek, firstLogDate } = getRequestsByWeek(parsedLogs, trendLineDateLimit);
    lineRequestsData = formatLineGraphData("Requests", requestsByWeek, trendLineDateLimit, firstLogDate); // format data for total requests (line graph)
    lineUniqueUsersData = formatLineGraphData("Unique Visitors", uniqueUsersByWeek, trendLineDateLimit, firstLogDate); // format data for unique visitors (line graph)
  }

  let topRequests = [];
  if (tipSheetRequestLogs.length > 0) {
    // calculate the top 50 tip sheet requests for the time period
    topRequests = getTopNRequestedTipSheets(tipSheetRequestLogs, dateLimit, 50);
  }

  const renderAnalyticsView = () => {
    switch (view) {
      case "table_users":
        return <UserUsageTable
          userData={userData}
        />;
      case "bar_users":
        return <Chart
          key={userData.length}
          chartType="BarChart"
          data={barUserData}
          options={barOptions}
          height={userData.length * 40 < 500 ? 500 : userData.length * 40}
        />;
      case "line_requests":
        return <Chart
          chartType="LineChart"
          data={lineRequestsData}
          options={lineRequestsOptions}
          height={500}
        />;
      case "line_unique_users":
        return <Chart
          chartType="LineChart"
          data={lineUniqueUsersData}
          options={lineUniqueUsersOptions}
          height={500}
        />;
      case "table_tipsheets":
        return <TipSheetUsageTable
          topRequests={topRequests}
        />;
      default:
        return <div>Error selecting table view.</div>;
    }
  }

  return (
    <div className="dashboard-table-container">
      <h3 className="dashboard-table-header-container">
        <div className="dashboard-table-header-buttons-container">
          <button className="dashboard-toggle-table-view-button" onClick={() => handleSwitchTableView('db')}>Show User Database</button>
          <div className="dashboard-toggle-include-admin-data">
            {includeAdminDataLoading ? (
              <div className="dashboard-spinner"></div>
            ) : (
              <input
                className="dashboard-toggle-checkbox"
                type="checkbox"
                id="include-admin-data"
                name="include-admin-data"
                checked={includeAdminData}
                onChange={handleToggleIncludeAdminData} />
            )}
            <label htmlFor="include-admin-data">Include admin data</label>
          </div>
        </div>
        <div>Analytics</div>
        <div className="dashboard-table-header-buttons-container">
          {view === 'table_users' || view === 'table_tipsheets' || view === 'line_requests' || view === 'line_unique_users'
            ? <button className="dashboard-export-csv-button" onClick={() => exportCSV(view, includeAdminData, dateLimit, trendLineDateLimit)}>Export</button>
            : <button style={{ visibility: "hidden" }}>export</button>
          }
          {view === 'line_requests' || view === 'line_unique_users'
            ?
            <select
              value={trendLineDateLimit}
              onChange={e => setTrendLineDateLimit(Number(e.target.value))}
              className="dashboard-analytics-select"
              id="trendline-date-select"
            >
              {trendLineDateOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            :
            <select
              value={dateLimit}
              onChange={e => setDateLimit(Number(e.target.value))}
              className="dashboard-analytics-select"
              id="table-date-select"
            >
              {dateOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          }

          <select
            value={view}
            onChange={e => setView(e.target.value)}
            className="dashboard-analytics-select"
            id="table-view-select"
          >
            {viewOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </h3>
      {loading
        ? null
        : renderAnalyticsView()
      }
    </div>
  );
};